import { get, set } from 'local-storage'
import {
  IColorData,
  IMeshData,
  IModelData,
  IModelDesignData,
  IVariantData,
} from '../../type/designType'
import { v4 as uuidv4 } from 'uuid'
import {
  IColor,
  ICompletedContextState,
  IModel,
  ITexture,
  IVariant,
} from '../../type/modelType'
import {
  changeColorByMeshName,
  changeTextureByMeshName,
  switchingVariant,
} from './customization'
import { loadModelConfig } from '../modelEngine/configuration'
import { VariantType } from '../../type/backendType'
import { getForceLandingPage, getUrlParams } from '../utils'

const storageKey = 'designs'

export const loadDesignData = async (
  model: IModel,
  context: ICompletedContextState,
  designId?: string | null
) => {
  if (designId) {
    const designState = await loadDesignState(designId, context)
    if (designState) {
      applyDesignToModel(model, designState.modelConfig)
      return
    }
  }

  const id = uuidv4()

  const newDesign = {
    id,
    modelConfig: modelToInitData(model),
  }

  saveDesignState(newDesign)
  const forceLanding = getForceLandingPage()
  document.location.replace(
    `?p=${model.id}&pd=${id}${forceLanding ? `&landing=${forceLanding}` : ''}`
  )
}

export const modelToInitData = (model: IModel): IModelData => {
  const meshes = model.meshes.map(mesh => {
    const variants = mesh.variants
      .map(variant => {
        return { id: variant.default ? variant.id : '' }
      })
      .filter(o => o.id !== '')
    const colors = mesh.colors
      ?.map(color => ({
        id: color.default ? color.id : '',
      }))
      .filter(o => o.id !== '')
    const textures = mesh.textures
      ?.map(texture => ({
        id: texture.default ? texture.id : '',
      }))
      .filter(o => o.id !== '')

    return {
      id: mesh.id,
      variants,
      ...(colors &&
        colors.length > 0 && {
          colors,
        }),
      ...(textures &&
        textures.length > 0 && {
          textures,
        }),
      ...(mesh.materialStyle && {
        materialStyle: mesh.materialStyle,
      }),
    }
  })
  return {
    modelId: model.id,
    meshes,
  }
}

export const applyDesignToModel = (
  model: IModel,
  designData: IModelData,
  force = false
) => {
  const allTextures: ITexture[] = []
  const allColors: IColor[] = []
  const allVariant: IVariant[] = []
  model.meshes.forEach(mesh => {
    if (mesh.textures) {
      allTextures.push(...mesh.textures)
    }
    if (mesh.colors) {
      allColors.push(...mesh.colors)
    }
    if (mesh.variants) {
      allVariant.push(...mesh.variants)
    }
  })

  designData.meshes.forEach(mesh => {
    const currentMesh = model.meshes.find(m => m.id === mesh.id)

    if (currentMesh) {
      const meshesName = currentMesh.variants.map(v => v.meshName)

      allVariant.forEach(variant => {
        if (mesh.variants.some(v => v.id === variant.id)) {
          switchingVariant(variant.meshName, currentMesh.variants)
        } else if (currentMesh.canHideMesh) {
          switchingVariant(false, currentMesh.variants)
        }
      })

      mesh.colors?.forEach(color => {
        const colorObject = allColors.find(a => a.id === color.id)
        if (colorObject) {
          changeColorByMeshName(colorObject, meshesName)
        }
      })

      mesh.textures?.forEach(texture => {
        const textureObject = allTextures.find(a => a.id === texture.id)
        if (textureObject) {
          changeTextureByMeshName(textureObject, meshesName)
        }
      })

      if (force) {
        if (!mesh.colors && !mesh.textures) {
          changeColorByMeshName(
            {
              id: 'white',
              title: 'white',
              hex: '#cccccc',
              variantType: VariantType.COLOR,
            },
            meshesName
          )
        }
      }
    }
  })
}

export const loadDesignState = async (
  designId: string,
  context: ICompletedContextState
): Promise<IModelDesignData | null> => {
  const designStates = get<IModelDesignData[]>(storageKey)
  if (designStates) {
    const design = designStates.find(d => d.id === designId)

    if (design) {
      const model = await loadModelConfig(design.modelConfig.modelId, context)

      if (model) {
        const meshIds = model.meshes.map(m => m.id)
        const designMeshes = design.modelConfig.meshes

        if (
          designMeshes.length === 0 ||
          designMeshes.some(
            m => !meshIds.includes(m.id) || m.variants.length === 0
          )
        ) {
          const initData = modelToInitData(model)
          return { id: design.id, modelConfig: initData }
        }
      }

      return design
    }
  }

  return null
}

export const saveDesignState = (design: IModelDesignData) => {
  const data = [
    ...(get<IModelDesignData[]>(storageKey) ?? []).filter(
      d => d.id !== design.id
    ),
    design,
  ]
  set<IModelDesignData[]>(storageKey, data)
}

export const removeDesignState = (designId: string) => {
  const data = [
    ...(get<IModelDesignData[]>(storageKey) ?? []).filter(
      d => d.id !== designId
    ),
  ]
  set<IModelDesignData[]>(storageKey, data)
}

export const removeDesignStatesInModel = (modelId: string) => {
  const data = [
    ...(get<IModelDesignData[]>(storageKey) ?? []).filter(
      d => d.modelConfig.modelId !== modelId
    ),
  ]
  set<IModelDesignData[]>(storageKey, data)
}

export const getAllDesignsState = (modelId: string): IModelDesignData[] => {
  const designStates = get<IModelDesignData[]>(storageKey)
  return designStates
    ? designStates.filter(d => d.modelConfig.modelId === modelId)
    : []
}

export const getProductDesignIdFromURL = (): string | null => {
  return getUrlParams('pd')
}
